import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import useSWRMutation from 'swr/mutation';
import { useShallow } from 'zustand/react/shallow';

import { confirmationEmailPost } from '@/api/auth';
import { ButtonClose } from '@/components/buttons';
import { showTooltip } from '@/helpers/tooltip';
import useEventListener from '@/hooks/eventListener';
import { adaptMutation } from '@/modules/common/helpers/swr';
import { useGetCommonStore } from '@/store';
import { TooltipTypes } from '@/store/tooltipStore';
import { ScContainer } from '@/styles/common';
import { ScEmailConfirmPopup } from './styled';

export const EmailConfirmPopup = () => {
  const { t } = useTranslation('common');
  const useCommonStore = useGetCommonStore();

  const {
    user,
    setEmailConfirmationPopupSeen,
    setEmailConfirmationPopupHeight,
  } = useCommonStore(
    useShallow((state) => ({
      user: state.user,
      setEmailConfirmationPopupSeen: state.setEmailConfirmationPopupSeen,
      setEmailConfirmationPopupHeight: state.setEmailConfirmationPopupHeight,
    }))
  );

  const { trigger: requestsConfirmationEmail } = useSWRMutation(
    'confirmationEmailPost',
    adaptMutation(confirmationEmailPost),
    {
      onSuccess: () => {
        setEmailConfirmationPopupSeen();
        showTooltip({
          type: TooltipTypes.SUCCESS,
          body: t('verifyEmailPopup.successTooltip'),
        });
      },
    }
  );

  const ref = useRef<HTMLDivElement>(null);

  const handleLinkClick = useCallback(() => {
    if (!user) return;
    requestsConfirmationEmail(user.email);
  }, [requestsConfirmationEmail, user]);

  useEventListener(
    'resize',
    () =>
      ref.current && setEmailConfirmationPopupHeight(ref.current.clientHeight),
    undefined,
    true
  );

  return (
    <ScEmailConfirmPopup ref={ref}>
      <ScContainer relative>
        <div className="text">
          {t('verifyEmailPopup.text')}
          <span className="link" onClick={handleLinkClick}>
            {t('verifyEmailPopup.linkText')}
          </span>
        </div>
        <ButtonClose
          dark
          isStatic
          onClick={setEmailConfirmationPopupSeen}
          small
        />
      </ScContainer>
    </ScEmailConfirmPopup>
  );
};
